// The voice page (pages/[model].js) and the sections only it renders
// (TryVoiceSection, LibraryDemosSection, ModelCommentSection/Item,
// ModelCollectionsWithVoice, AddToCollectionModal). One template, ~90,000
// pages, so the SEO strings matter: they follow what people search ("voz de
// goku ia", "voz ia de ...", BR "voz do ... ia"). Portuguese avoids "do/da"
// because the voice's gender is unknown. See messages/README.md.
export const voice = {
  en: {
    // <head> and JSON-LD
    "voice.seo.title": "{name} AI Voice: Hear It & Make AI Covers | Jammable",
    "voice.seo.description":
      "Listen to {name} AI covers and make your own in about a minute. Use the community-made {name} voice for songs, text-to-speech and duets on Jammable.",
    "voice.seo.appName": "{name} AI Voice Generator",
    "voice.seo.private": "Private voice | Jammable",
    "voice.name": "{name} AI Voice",

    // Removed / private
    "voice.removed": "This voice has been removed.",
    "voice.private": "This voice is private.",
    "voice.browseOther": "Browse other voices",

    "voice.error": "Something went wrong. Please try again",
    "voice.copied": "Copied to clipboard!",

    // Hero
    "voice.newer.body":
      "{username} has updated this voice. Try the newer version",
    "voice.newer.cta": "Newer version",
    "voice.communityFavourite": "Community favourite",
    // {name} is replaced by the uploader's name, styled separately.
    "voice.uploadedBy": "Uploaded By {name}",
    "voice.voices.one": "{n} voice",
    "voice.voices.other": "{n} voices",
    "voice.createCover": "Create a cover · {uses} uses",
    "voice.like": "Like voice",
    "voice.unlike": "Unlike voice",
    "voice.comments": "Comments",
    "voice.saveToCollection": "Save to collection",
    "voice.share": "Share Voice",
    "voice.edit": "Edit Voice",
    "voice.report": "Report Voice",
    "voice.delete": "Delete Voice",
    "voice.more": "More",
    "voice.description": "Description",

    "voice.discord.label": "Talk {name} with the community",
    "voice.discord.labelNoName": "Talk AI voices with the community",
    "voice.discord.body":
      "Share your covers, request new voices and get tips on Discord",

    // Related voices. {badge} is the voice's category as stored (English).
    "voice.similar.title": "Similar {name} AI Voices",
    "voice.similar.sub": "Users commonly used these voices with {name}.",
    "voice.similar.moreHub": "More {badge} voices",
    "voice.popularHub.title": "Popular {badge} AI voices",
    "voice.seeAll": "See all",
    "voice.related.title": "More voices like {name}",
    "voice.weights.callout":
      "Looking for Weights AI? Weights.gg has wound down.",
    "voice.weights.calloutLink": "See the Weights AI alternative",
    "voice.otherVersions.title": "Other versions of {name}",
    "voice.collections.title": "Collections with {name}",
    "voice.collections.titleNoName": "Collections With This Voice",
    "voice.pairWell.title": "Voices that pair well with {name}",

    // About
    "voice.about.title": "About the {name} AI Voice",
    "voice.about.use":
      "Use the {name} AI voice to create AI covers, as a voice changer, or to generate text-to-speech.",
    "voice.about.categorised": "It's categorised as {list}.",
    "voice.about.uses": "This voice has been used {n} times on Jammable.",
    "voice.howTo.title": "How to make a {name} AI cover",

    // FAQ
    "voice.faq.title": "{name} AI Voice FAQ",
    "voice.faq.sub": "Everything about making covers with the {name} voice.",
    "voice.link.aiCover": "AI Cover Generator",
    "voice.link.voiceChanger": "AI Voice Changer",
    "voice.link.browseAll": "Browse all AI voices",
    "voice.link.weights": "Weights.gg alternative",
    "voice.faq.how.q": "How do I make {name} AI covers?",
    // {dmca} becomes the DMCA Policy link.
    "voice.faq.how.a":
      "In order to make {name} AI covers, you need to add your own audio in the studio (please read our {dmca}). Once you've done so, press ready to convert! Your {name} AI cover will be ready in around 30-60 seconds.",
    "voice.faq.dmca": "DMCA Policy",
    "voice.faq.input.q": "What input can I use for my {name} AI covers?",
    "voice.faq.input.a":
      "You can only upload your own original audio. You should not input any sound recordings that contain third party copyright material without the owner's permission.",
    "voice.faq.time.q": "How long will my {name} AI cover take?",
    "voice.faq.time.a":
      "Your {name} AI cover will be ready in around 30-60 seconds!",
    "voice.faq.credits.q": "How many credits does each {name} AI cover use?",
    "voice.faq.credits.a": "Each {name} AI cover will use just 1 credit!",
    "voice.faq.acapella.q": "Do I need to upload an acapella?",
    "voice.faq.acapella.a":
      "No, you do not need to upload an acapella as our system will extract the acapella regardless for your {name} AI cover!",
    "voice.faq.changer.q": "Can I use the {name} AI voice as a voice changer?",
    "voice.faq.changer.a":
      "Yes. You can use the {name} AI voice as a voice changer or for text-to-speech — upload or record your audio and convert it into the {name} voice in seconds.",
    "voice.faq.free.q": "Is the {name} AI voice free?",
    "voice.faq.free.a":
      "You can listen to the {name} AI voice demos and set up a cover for free. Making covers needs a Jammable plan, which gives you unlimited {name} AI covers, downloads and more.",

    // TryVoiceSection
    "voice.demos.title": "Hear it first",
    "voice.demos.note":
      "Demos are auto-generated to show the quality of voices.",
    "voice.demos.maleSinging": "Male Singing {n}",
    "voice.demos.femaleSinging": "Female Singing {n}",
    "voice.demos.english": "English",
    "voice.play": "Play {name}",
    "voice.pause": "Pause {name}",

    // LibraryDemosSection
    "voice.library.title": "Songs with this voice",
    "voice.library.sub":
      "Listen to the AI {name} voice singing popular songs from our library.",
    "voice.library.unknownSong": "Unknown Song",
    "voice.library.unknownArtist": "Unknown Artist",

    // ModelCommentSection / ModelCommentItem
    "voice.comments.count.one": "{n} comment",
    "voice.comments.count.other": "{n} comments",
    "voice.comments.add": "Add a comment",
    "voice.comments.post": "Post",
    "voice.comments.top": "Top",
    "voice.comments.newest": "Newest",
    "voice.comments.empty": "No comments here...",
    "voice.comments.emptySub": "Start the conversation!",
    "voice.comments.more": "Show more comments",
    "voice.comments.posted": "Comment posted!",
    "voice.comments.avatarAlt": "User Avatar",
    "voice.comments.creator": "Creator",
    "voice.comments.pinned": "Pinned",
    "voice.comments.reply": "Reply",
    "voice.comments.replyLabel": "Comment Reply",
    "voice.comments.replyPlaceholder": "What do you think?!",
    "voice.comments.viewReply.one": "View {n} Reply",
    "voice.comments.viewReply.other": "View {n} Replies",
    "voice.comments.viewMore": "View {n} More",
    "voice.comments.justNow": "just now",
    "voice.comments.minutes": "{n}m",
    "voice.comments.hours": "{n}h",
    "voice.comments.days": "{n}d",
    "voice.comments.repliesFailed": "Failed to load replies",
    "voice.comments.pinnedToast": "Comment pinned!",
    "voice.comments.unpinnedToast": "Comment unpinned!",
    "voice.comments.pinFailed": "Failed to pin comment",
    "voice.comments.pin": "Pin Comment",
    "voice.comments.unpin": "Unpin Comment",
    "voice.comments.delete": "Delete Comment",
    "voice.comments.report": "Report Comment",

    // AddToCollectionModal
    "voice.addTo.title": "Add to Collection",
    "voice.addTo.error": "Error loading collections: {message}",
    "voice.addTo.empty": "No collections found...",
    "voice.addTo.emptySub": "Create your first collection!",
    "voice.addTo.new": "New Collection",
  },
};
